import API_BASE_URL from "../config";
import { getToken } from "./authService";
import filterService from './filterService';

let productsPromise = null;

export async function getProducts() {
  if (productsPromise) return productsPromise;
  
  productsPromise = fetch(`${API_BASE_URL}/products`)
    .then(res => res.json())
    .catch(err => {
      productsPromise = null; 
      throw err;
    })
    .finally(() => {
      // Clear cache after a short delay so we can fetch fresh data later
      // This solves the initial load race condition but allows updates.
      setTimeout(() => { productsPromise = null; }, 5000);
    });
    
  return productsPromise;
}

export async function getProductById(id) {
  const res = await fetch(`${API_BASE_URL}/products/${id}`);
  return res.json();
}

export async function getBrandsWithImages() {
  const res = await fetch(`${API_BASE_URL}/products/brands/detailed`);
  if (!res.ok) throw new Error('Failed to load brands');
  return res.json();
}

export async function getCategoriesAndBrands(opts = {}) {
  const qs = new URLSearchParams();
  if (opts.includeInactive) qs.set('includeInactive', 'true');
  if (opts.includeOutOfStock) qs.set('includeOutOfStock', 'true');
  const res = await fetch(`${API_BASE_URL}/products/categories/list${qs.toString() ? `?${qs.toString()}` : ''}`);
  return res.json();
}

// Get categories and brands, optionally scoped to a category name
export async function getCategoriesAndBrandsScoped(category, opts = {}) {
  const qs = new URLSearchParams();
  const debug = typeof opts === 'boolean' ? opts : false;
  const options = typeof opts === 'object' ? opts : {};
  if (category) qs.set('category', category);
  if (debug || options.debug) qs.set('debug', '1');
  if (options.includeInactive) qs.set('includeInactive', 'true');
  if (options.includeOutOfStock) qs.set('includeOutOfStock', 'true');
  const res = await fetch(`${API_BASE_URL}/products/categories/list${qs.toString() ? `?${qs.toString()}` : ''}`);
  return res.json();
}

export async function searchProducts(params = {}, fetchOptions = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    // If value is an array, append each element separately (brand[]=a&brand[]=b style)
    if (Array.isArray(v)) {
      v.forEach(item => { if (item !== undefined && item !== null && item !== '') qs.append(k, item); });
    } else {
      qs.append(k, v);
    }
  });
  const url = `${API_BASE_URL}/products?${qs.toString()}`;
  const res = await fetch(url, fetchOptions);
  if (!res.ok) {
    let err = 'Failed to load products';
    try { const body = await res.json(); if (body && (body.error || body.message)) err = body.error || body.message; } catch (_) { }
    throw new Error(err);
  }
  return res.json();
}

// Convenience: search using Filters component's filter object
export async function searchWithFilters(filters = {}) {
  const params = filterService.buildParams(filters || {});
  return searchProducts(params);
}

export async function getFilteredProducts(params = {}, fetchOptions = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v)) {
      v.forEach(item => { if (item !== undefined && item !== null && item !== '') qs.append(k, item); });
    } else {
      qs.append(k, v);
    }
  });
  const url = `${API_BASE_URL}/products/filter?${qs.toString()}`;
  const res = await fetch(url, fetchOptions);
  if (!res.ok) {
    let err = 'Failed to load filtered products';
    try { const body = await res.json(); if (body && (body.error || body.message)) err = body.error || body.message; } catch (_) { }
    throw new Error(err);
  }
  return res.json();
}

export async function addProduct(productData) {
  const token = getToken();
  const form = new FormData();
  Object.entries(productData).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    // If it's an array, it may contain File objects and/or existing URLs.
    if (Array.isArray(value)) {
      // special-case images handled elsewhere; otherwise serialize arrays as JSON
      if (key === 'images') {
        const files = value.filter(v => v instanceof File);
        const nonFiles = value.filter(v => !(v instanceof File));
        if (files.length) for (const f of files) form.append('images', f);
        if (nonFiles.length) form.append('existingImages', JSON.stringify(nonFiles));
        return;
      }
      form.append(key, JSON.stringify(value));
      return;
    }

    // If it's a FileList, append each file
    if (value instanceof FileList) {
      for (const f of value) form.append('images', f);
      return;
    }

    // If it's a plain object (e.g., specs), stringify it
    if (typeof value === 'object' && !(value instanceof File)) {
      form.append(key, JSON.stringify(value));
    } else {
      form.append(key, value);
    }
  });
  const res = await fetch(`${API_BASE_URL}/products`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: form,
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error || 'Failed to create product');
  }
  return res.json();
}

export async function updateProduct(id, productData) {
  const token = getToken();
  const form = new FormData();
  Object.entries(productData).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    // If it's an array, it may contain File objects and/or existing URLs.
    if (Array.isArray(value)) {
      if (key === 'images') {
        const files = value.filter(v => v instanceof File);
        const nonFiles = value.filter(v => !(v instanceof File));
        if (files.length) for (const f of files) form.append('images', f);
        if (nonFiles.length) form.append('existingImages', JSON.stringify(nonFiles));
        return;
      }
      form.append(key, JSON.stringify(value));
      return;
    }

    if (value instanceof FileList) {
      for (const f of value) form.append('images', f);
      return;
    }

    if (typeof value === 'object' && !(value instanceof File)) {
      form.append(key, JSON.stringify(value));
    } else {
      form.append(key, value);
    }
  });
  const res = await fetch(`${API_BASE_URL}/products/${id}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: form,
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error || 'Failed to update product');
  }
  return res.json();
}

export async function deleteProduct(id) {
  const token = getToken();
  const res = await fetch(`${API_BASE_URL}/products/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error || 'Failed to delete product');
  }
  return res.json();
}

export async function rateProduct(id, value) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE_URL}/products/${id}/rate`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ value }),
  });
  return res.json();
}
